// Offline→online recovery edge detection (wiki/feature-offline-fallback-cues.md
// §1). `backendHealth.store` starts optimistically `online: true`, flips false
// on a failed probe, and back true when a probe later succeeds. Boot-time
// global fetches (whoami via the auth store, the `/api/config/auth`
// feature-flags fetch) are fired once at mount; if the backend is down at boot
// they are stranded — the auth store stays `'unknown'` (the top-right
// AccountMenu renders nothing) and the feature flags stay at the all-off
// default. Nothing re-runs them when the backend later returns.
//
// This tiny helper isolates the ONE bit of logic that needs testing: detecting
// a genuine `online: false → true` transition (and ONLY that — never the
// initial optimistic `online: true`, never an offline edge). The root layout
// wires it to `backendHealth.store` and, on a recovery edge, re-runs the
// stranded boot fetches.

import type { Readable } from 'svelte/store';
import type { BackendHealthState } from './backendHealth';

/**
 * Subscribe to a backend-health store and invoke `onRecover` exactly on each
 * `online: false → true` transition.
 *
 * The FIRST value the store emits on subscribe (its current value) seeds the
 * previous-online tracker WITHOUT firing — so the initial optimistic
 * `online: true` never triggers a recovery, and neither does any value that
 * doesn't change `online`. Only a real false→true edge fires `onRecover`.
 *
 * @returns the store's unsubscribe fn, for cleanup (e.g. onMount's teardown).
 */
export function onBackendRecover(
  store: Readable<BackendHealthState>,
  onRecover: () => void,
): () => void {
  // `undefined` until the first emission seeds it — so the seed itself is never
  // treated as a transition regardless of whether boot is online or offline.
  let prevOnline: boolean | undefined;
  return store.subscribe((state) => {
    const wasOffline = prevOnline === false;
    prevOnline = state.online;
    if (wasOffline && state.online) {
      onRecover();
    }
  });
}
